/**
 * Analytics event catalog.
 *
 * Event names, tables, producer surfaces, and bounded property schemas are
 * generated from `schema/analytics-events.schema.json`. This module owns the
 * runtime validators and call-site aliases. Durable product state belongs in
 * its owning Postgres or ClickHouse table and should be joined at read time.
 *
 * Unknown-field contract:
 * - Current producers (TypeScript capture and generated CLI definitions)
 *   cannot send a property the registry does not define. `validateAnalyticsEventProperties`
 *   rejects an undefined key, a wrong type, or an oversized value before
 *   any row is written. Events that opt into `additionalProperties` are the
 *   documented exception.
 * - Authenticated ingest of already-released clients ignores unknown fields
 *   and lands the defined ones. Only type-invalid values on defined fields
 *   reject the request. Old binaries must keep working when the registry
 *   drops or renames a field.
 * The anonymous public endpoint is a different contract: it accepts only a
 * fixed field list and rejects unknown fields.
 */

import { ANALYTICS_EVENT_CATALOG } from "./event-catalog.generated";

export { ANALYTICS_EVENT_CATALOG };

export const QUERY_ACTIVITY_TEXT_MAX_BYTES =
  ANALYTICS_EVENT_CATALOG["query.succeeded"].properties.query_text.maxBytes;

/**
 * Truncate without splitting a UTF-8 code point. Capture uses this before
 * either query-activity leg is enqueued.
 */
export function truncateUtf8(value: string, maxBytes: number): string {
  const encoder = new TextEncoder();
  const encoded = encoder.encode(value);
  if (encoded.byteLength <= maxBytes) return value;
  if (maxBytes <= 0) return "";

  let start = maxBytes - 1;
  while (start > 0 && ((encoded[start] ?? 0) & 0xc0) === 0x80) start -= 1;
  const lead = encoded[start] ?? 0;
  const expectedLength = lead < 0x80 ? 1 : lead < 0xe0 ? 2 : lead < 0xf0 ? 3 : 4;
  const end = maxBytes - start < expectedLength ? start : maxBytes;
  return new TextDecoder().decode(encoded.slice(0, end));
}

export type AnalyticsEventTable = "web_conversions" | "platform_events" | "cli_events";
export type AnalyticsProducerSurface = "web" | "server" | "cli";

type StringProperty = {
  type: "string";
  maxBytes: number;
  optional?: true;
  nullable?: true;
  values?: readonly string[];
};

type NumberProperty = {
  type: "number";
  optional?: true;
  nullable?: true;
  integer?: true;
  min?: number;
  max?: number;
};

type BooleanProperty = { type: "boolean"; optional?: true };

type StringArrayProperty = {
  type: "string[]";
  maxItems: number;
  maxItemBytes: number;
  optional?: true;
};

type PropertyDefinition = StringProperty | NumberProperty | BooleanProperty | StringArrayProperty;

export type AnalyticsEventName = keyof typeof ANALYTICS_EVENT_CATALOG;
export type PlatformEventName = {
  [Name in AnalyticsEventName]: (typeof ANALYTICS_EVENT_CATALOG)[Name]["table"] extends "platform_events"
    ? Name
    : never;
}[AnalyticsEventName];
export type WebConversionEventName = {
  [Name in AnalyticsEventName]: (typeof ANALYTICS_EVENT_CATALOG)[Name]["table"] extends "web_conversions"
    ? Name
    : never;
}[AnalyticsEventName];

type InferProperty<Definition extends PropertyDefinition> = Definition extends StringProperty
  ? Definition["nullable"] extends true
    ? string | null
    : Definition extends { values: readonly (infer Value extends string)[] }
      ? Value
      : string
  : Definition extends NumberProperty
    ? Definition["nullable"] extends true
      ? number | null
      : number
    : Definition extends BooleanProperty
      ? boolean
      : Definition extends StringArrayProperty
        ? string[]
        : never;

type PropertySchema<Name extends AnalyticsEventName> =
  (typeof ANALYTICS_EVENT_CATALOG)[Name]["properties"];
type OptionalPropertyKeys<Name extends AnalyticsEventName> = {
  [Key in keyof PropertySchema<Name>]: PropertySchema<Name>[Key] extends { optional: true }
    ? Key
    : never;
}[keyof PropertySchema<Name>];
type RequiredPropertyKeys<Name extends AnalyticsEventName> = Exclude<
  keyof PropertySchema<Name>,
  OptionalPropertyKeys<Name>
>;

export type AnalyticsEventProperties<Name extends AnalyticsEventName> = {
  [Key in RequiredPropertyKeys<Name>]: PropertySchema<Name>[Key] extends PropertyDefinition
    ? InferProperty<PropertySchema<Name>[Key]>
    : never;
} & {
  [Key in OptionalPropertyKeys<Name>]?: PropertySchema<Name>[Key] extends PropertyDefinition
    ? InferProperty<PropertySchema<Name>[Key]>
    : never;
} & ((typeof ANALYTICS_EVENT_CATALOG)[Name] extends { additionalProperties: unknown }
    ? Record<string, unknown>
    : unknown);

export function postHogEventName(name: AnalyticsEventName): string {
  return ANALYTICS_EVENT_CATALOG[name].postHog;
}

export function isAnalyticsEventName(value: string): value is AnalyticsEventName {
  return Object.hasOwn(ANALYTICS_EVENT_CATALOG, value);
}

export function isWebConversionEventName(value: string): value is WebConversionEventName {
  return isAnalyticsEventName(value) && ANALYTICS_EVENT_CATALOG[value].table === "web_conversions";
}

export function validateAnalyticsEventProperties<Name extends AnalyticsEventName>(
  name: Name,
  properties: unknown,
): asserts properties is AnalyticsEventProperties<Name> {
  if (!isRecord(properties)) {
    throw new Error(`analytics event ${name} properties must be an object`);
  }
  const event = ANALYTICS_EVENT_CATALOG[name];
  const schema: Record<string, PropertyDefinition> = event.properties;
  const keys = Object.keys(properties);
  const additional = "additionalProperties" in event ? event.additionalProperties : undefined;
  if (additional && keys.length > additional.maxKeys) {
    throw new Error(`analytics event ${name} has too many properties`);
  }
  if (additional && utf8Bytes(JSON.stringify(properties)) > additional.maxBytes) {
    throw new Error(`analytics event ${name} properties exceed ${additional.maxBytes} bytes`);
  }

  for (const [key, definition] of Object.entries(schema)) {
    const value = properties[key];
    if (value === undefined) {
      if (!definition.optional) {
        throw new Error(`analytics event ${name} requires property ${key}`);
      }
      continue;
    }
    validateProperty(name, key, value, definition);
  }
  for (const key of keys) {
    if (!(key in schema) && !additional) {
      throw new Error(`analytics event ${name} does not allow property ${key}`);
    }
  }
}

function validateProperty(
  name: AnalyticsEventName,
  key: string,
  value: unknown,
  definition: PropertyDefinition,
): void {
  if (definition.type === "string") {
    if (value === null && definition.nullable) return;
    if (typeof value !== "string") {
      throw new Error(`analytics event ${name} property ${key} must be a string`);
    }
    const bytes = utf8Bytes(value);
    if (bytes > definition.maxBytes) {
      throw new Error(
        `analytics event ${name} property ${key} exceeds ${definition.maxBytes} bytes`,
      );
    }
    if (definition.values && !definition.values.includes(value)) {
      throw new Error(`analytics event ${name} property ${key} has an invalid value`);
    }
    return;
  }
  if (definition.type === "number") {
    if (value === null && definition.nullable) return;
    if (
      typeof value !== "number" ||
      !Number.isFinite(value) ||
      (definition.integer && !Number.isInteger(value)) ||
      (definition.min !== undefined && value < definition.min) ||
      (definition.max !== undefined && value > definition.max)
    ) {
      throw new Error(`analytics event ${name} property ${key} must be a bounded number`);
    }
    return;
  }
  if (definition.type === "boolean") {
    if (typeof value !== "boolean") {
      throw new Error(`analytics event ${name} property ${key} must be a boolean`);
    }
    return;
  }
  if (
    !Array.isArray(value) ||
    value.length > definition.maxItems ||
    value.some((item) => typeof item !== "string" || utf8Bytes(item) > definition.maxItemBytes)
  ) {
    throw new Error(`analytics event ${name} property ${key} must be a bounded string array`);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function utf8Bytes(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

/** Compatibility view for parity jobs; mapping ownership stays in this catalog. */
export const ANALYTICS_EVENT_PAIRS = Object.values(ANALYTICS_EVENT_CATALOG)
  .filter(
    (
      event,
    ): event is typeof event & {
      parity: true;
      postHog: string;
    } => "parity" in event && event.parity === true,
  )
  .map((event) => ({
    postHog: event.postHog,
    clickHouse: event.clickHouse,
    table: event.table,
  }));

export type AnalyticsEventPair = (typeof ANALYTICS_EVENT_PAIRS)[number];

/** Stable call-site names derived from the catalog's logical event keys. */
export const TelemetryEvent = {
  PAGE_VIEW: "$pageview",
  PAGELEAVE: "pageleave",
  ELEMENT_INTERACTION: "element_interaction",
  WAITLIST_MODAL_OPENED: "waitlist_modal_opened",
  WAITLIST_FORM_SUBMITTED: "waitlist_form_submitted",
  WAITLIST_SIGNED_UP: "waitlist_signed_up",
  SIGNUP_CTA_CLICKED: "signup_cta_clicked",
  CONTACT_FORM_SUBMITTED: "contact_form_submitted",
  CONTACT_MEETING_BOOKED: "contact_meeting_booked",
  USER_SIGNED_UP: "account.created",
  ORG_CREATED: "org.created",
  ORG_INVITATION_CREATED: "org.invite.created",
  ORG_INVITATION_ACCEPTED: "org.invite.accepted",
  ORG_MEMBERSHIP_CREATED: "org.member.added",
  ORG_MEMBERSHIP_REMOVED: "org.member.removed",
  BANNER_CTA_CLICKED: "banner_cta_clicked",
  BANNER_DISMISSED: "banner_dismissed",
  COMMAND_CENTER_OPENED: "command_center_opened",
  COMMAND_CENTER_COMMAND_SELECTED: "command_center_command_selected",
  ORG_AUTH_RECOVERY: "axp_org_auth_recovery",
  ORG_AUTH_FINAL_FAILURE: "axp_org_auth_final_failure",
  REALTIME_CHANNEL_STATUS: "axp_realtime_channel_status",
  REALTIME_RECONNECT_SCHEDULED: "axp_realtime_reconnect_scheduled",
  REALTIME_GAP_CATCHUP: "axp_realtime_gap_catchup",
  REALTIME_TRANSPORT_DISCONNECTED: "axp_realtime_transport_disconnected",
  REALTIME_AUTH_MISSING: "axp_realtime_auth_missing",
  REALTIME_CHANNEL_OPEN_FAILED: "axp_realtime_channel_open_failed",
  REALTIME_PAYLOAD_APPLY_FAILED: "axp_realtime_payload_apply_failed",
} as const satisfies Record<string, AnalyticsEventName | "$pageview">;
